import { schema } from "normalizr";
import {
  EmployeePolicy,
  EmployeeForm,
  VisionPlan,
  DentalPlan,
  MedicalPlan,
  CarrierConfiguration,
  RecommendationSurvey,
  EmployeeDependent,
  Claim,
  EmployerForm,
  CompanyProfile,
  CompanyPolicy,
  Account,
  Invoice,
  CompanyApplication,
} from "Utils/types";

import { formFormatDate } from "Common/forms/utils";

export const medicalPlanSchema = new schema.Entity<MedicalPlan>("medicalPlans");
export const dentalPlanSchema = new schema.Entity<DentalPlan>("dentalPlans");
export const visionPlanSchema = new schema.Entity<VisionPlan>("visionPlans");
export const carrierConfigurationSchema = new schema.Entity<CarrierConfiguration>("carrierConfigurations");
export const recommendationSurveySchema = new schema.Entity<RecommendationSurvey>("recommendationSurveys");
export const employeeDependentSchema = new schema.Entity<EmployeeDependent>(
  "employeeDependents",
  {},
  {
    processStrategy: (entity) => ({
      ...entity,
      bornAt: entity.bornAt ? new Date(entity.bornAt) : null,
    }),
  },
);
export const claimSchema = new schema.Entity<Claim>("claims");
export const employeeFormProcessStrategy = (entity) => ({
  ...entity,
  bornAt: entity.bornAt ? new Date(entity.bornAt) : null,
  hiredAt: entity.hiredAt ? new Date(entity.hiredAt) : null,
  renewalDate: entity.renewalDate ? new Date(entity.renewalDate) : null,
});
const employeeFormSchema = new schema.Entity<EmployeeForm>(
  "employeeForms",
  {
    employeeDependents: [employeeDependentSchema],
    claims: [claimSchema],
  },
  {
    processStrategy: employeeFormProcessStrategy,
  },
);
export const employeePolicyProcessStrategy = (entity) => ({
  ...entity,
  requestedCancellationDate: entity.requestedCancellationDate ? new Date(entity.requestedCancellationDate) : null,
});
export const employeePolicySchema = new schema.Entity<EmployeePolicy>(
  "employeePolicies",
  {
    employeeForm: employeeFormSchema,
    medicalPlan: medicalPlanSchema,
    dentalPlan: dentalPlanSchema,
    visionPlan: visionPlanSchema,
  },
  {
    processStrategy: employeePolicyProcessStrategy,
  },
);
employeeFormSchema.define({
  enrolledEmployeePolicy: employeePolicySchema,
  upcomingEmployeePolicy: employeePolicySchema,
});
export { employeeFormSchema };
export const employerFormSchema = new schema.Entity<EmployerForm>(
  "employerForms",
  {},
  {
    processStrategy: (entity) => ({
      ...entity,
      dateEstablishedAt: entity.dateEstablishedAt ? new Date(entity.dateEstablishedAt) : null,
      payrollStartDate: entity.payrollStartDate ? new Date(entity.payrollStartDate) : null,
    }),
  },
);
export const companyProfileProcessStrategy = (entity) => ({
  ...entity,
  ...(entity.renewalDate !== undefined && {
    renewalDate: entity.renewalDate ? new Date(entity.renewalDate) : null,
  }),
});
export const companyProfileSchema = new schema.Entity<CompanyProfile>(
  "companyProfiles",
  {
    employeeForms: [employeeFormSchema],
    employerForm: employerFormSchema,
  },
  {
    processStrategy: companyProfileProcessStrategy,
  },
);
const companyContributionSchema = new schema.Entity("companyContributions");
export const companyPolicySchema = new schema.Entity<CompanyPolicy>(
  "companyPolicies",
  {
    companyProfile: companyProfileSchema,
    medicalPlans: [medicalPlanSchema],
    dentalPlans: [dentalPlanSchema],
    visionPlans: [visionPlanSchema],
    employeePolicies: [employeePolicySchema],
    companyContributions: [companyContributionSchema],
  },
  {
    processStrategy: (entity) => ({
      ...entity,
      submittedAt: entity.submittedAt ? new Date(entity.submittedAt) : null,
      // We shouldn't be using formFormatDate here.  Process the dates into Date objects, then use formFormat
      // at the component level.
      coverageStartDate: entity.coverageStartDateAt ? formFormatDate(new Date(entity.coverageStartDateAt)) : "",
      typeOfContribution: entity.typeOfContribution && entity.typeOfContribution === "fixed" ? "A flat dollar amount" : "A percentage",
    }),
  },
);
companyProfileSchema.define({
  upcomingCompanyPolicy: companyPolicySchema,
});
export const accountSchema = new schema.Entity<Account>("accounts", {
  enrolledCompanyPolicy: companyPolicySchema,
  upcomingCompanyPolicy: companyPolicySchema,
  cancelledCompanyPolicy: companyPolicySchema,
  enrolledEmployeePolicy: employeePolicySchema,
  upcomingEmployeePolicy: employeePolicySchema,
});
export const invoiceSchema = new schema.Entity<Invoice>("invoices");
export const companyApplicationSchema = new schema.Entity<CompanyApplication>(
  "companyApplications",
  {},
  {
    processStrategy: (entity) => ({
      ...entity,
      dateEstablishedAt: entity.dateEstablishedAt ? formFormatDate(new Date(entity.dateEstablishedAt)) : null,
      payrollStartDate: entity.payrollStartDate ? formFormatDate(new Date(entity.payrollStartDate)) : null,
      coverageStartDateAt: entity.coverageStartDateAt ? formFormatDate(new Date(entity.coverageStartDateAt)) : null,
    }),
  },
);
